import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, Button, Upload } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import moment from "moment";
import { useDispatch } from "react-redux";
import { updateTicketContent } from "../../../redux/rtk/features/crm/ticket/ticketSlice";
import baseUrl from "../../../utils/baseUrl";
import {
  IoCreateOutline,
  IoDocumentTextOutline,
  IoPeopleOutline,
  IoCalendarOutline,
  IoCloudUploadOutline,
  IoCloseCircle,
  IoImagesOutline,
} from "react-icons/io5";
import "./EditPostModal.css";

const { Option } = Select;
const { Dragger } = Upload;

const postForOptions = [
  { label: "Public", value: 1 },
  { label: "Participants", value: 2 },
  { label: "Faculty/Staff", value: 3 },
];

// Edit modal for a post's own content — separate from the admin approval
// workflow, which is driven by ticketStatusId elsewhere and untouched here.
const EditPostModal = ({ ticket, open, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  // Existing attachments stay visible (with an Undo option) even after being
  // marked for removal, so the user can see what they're about to lose.
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  useEffect(() => {
    if (ticket && open) {
      form.setFieldsValue({
        subject: ticket.subject,
        postFor: ticket.postFor,
        eventDate: ticket.eventDate ? moment(ticket.eventDate) : null,
      });
      setDescription(ticket.description || "");
      setExistingImages(ticket.images || []);
      setRemovedImageIds([]);
      setNewFiles([]);
    }
  }, [ticket, open, form]);

  const toggleRemoveExisting = (imageId) => {
    setRemovedImageIds((prev) =>
      prev.includes(imageId) ? prev.filter((id) => id !== imageId) : [...prev, imageId]
    );
  };

  const handleFinish = async (values) => {
    setSaving(true);

    const formData = new FormData();
    formData.append("subject", values.subject);
    formData.append("description", description);
    formData.append("postFor", values.postFor);
    formData.append("eventDate", values.eventDate ? values.eventDate.format("YYYY-MM-DD") : "");
    formData.append("removedImageIds", JSON.stringify(removedImageIds));
    newFiles.forEach((file) => {
      if (file.originFileObj) formData.append("files", file.originFileObj);
    });

    const resp = await dispatch(updateTicketContent({ id: ticket.ticketId, formData }));
    setSaving(false);
    if (resp.payload?.message === "success") {
      onSaved?.();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      className="edit-post-modal"
      destroyOnClose
    >
      <div className="edit-post-modal-header">
        <span className="edit-post-modal-icon">
          <IoCreateOutline />
        </span>
        <div>
          <h3>Edit Post</h3>
          <p>Update the details of your post below.</p>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleFinish} className="edit-post-form">
        <Form.Item
          name="subject"
          label={
            <span className="edit-post-label">
              <IoDocumentTextOutline /> Subject
            </span>
          }
          rules={[{ required: true, message: "Please enter the subject" }]}
        >
          <Input size="large" placeholder="Post subject" />
        </Form.Item>

        <Form.Item
          label={
            <span className="edit-post-label">
              <IoDocumentTextOutline /> Description
            </span>
          }
        >
          <ReactQuill
            theme="snow"
            value={description}
            onChange={setDescription}
            placeholder="Post content"
          />
        </Form.Item>

        <div className="edit-post-row">
          <Form.Item
            name="postFor"
            label={
              <span className="edit-post-label">
                <IoPeopleOutline /> Post For
              </span>
            }
            rules={[{ required: true, message: "Please select an audience" }]}
          >
            <Select size="large" placeholder="Select audience">
              {postForOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="eventDate"
            label={
              <span className="edit-post-label">
                <IoCalendarOutline /> Event Date <span className="edit-post-optional">(optional)</span>
              </span>
            }
          >
            <DatePicker size="large" format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item
          label={
            <span className="edit-post-label">
              <IoImagesOutline /> Attachments
            </span>
          }
        >
          {existingImages.length > 0 && (
            <div className="edit-post-existing-images">
              {existingImages.map((image) => {
                const isRemoved = removedImageIds.includes(image.id);
                return (
                  <div
                    key={image.id}
                    className={`edit-post-image-chip${isRemoved ? " is-removed" : ""}`}
                  >
                    <img src={`${baseUrl}/files/${image.imageName}`} alt="Attachment" />
                    <button
                      type="button"
                      className="edit-post-image-toggle"
                      onClick={() => toggleRemoveExisting(image.id)}
                    >
                      {isRemoved ? "Undo" : <IoCloseCircle />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <Dragger
            accept=".png,.jpg,.jpeg"
            multiple
            beforeUpload={() => false}
            fileList={newFiles}
            onChange={({ fileList }) => setNewFiles(fileList)}
            className="edit-post-dragger"
          >
            <p className="ant-upload-drag-icon">
              <IoCloudUploadOutline />
            </p>
            <p className="ant-upload-text">Add new photos</p>
            <p className="ant-upload-hint">PNG, JPG, JPEG size must less than 1 mb</p>
          </Dragger>
        </Form.Item>

        <Form.Item className="edit-post-submit-wrap">
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={saving}
            className="edit-post-submit"
          >
            Save Changes
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPostModal;
